import type {TurnResponse} from '@wandb/weave/lib/WbAgent/WbAgentClient';
import {produce} from 'immer';

import {decodePromptForThreadTitle} from '../components/WBAgent/utils';
import {terminalEventForTurnState, TurnEventExtractor} from './runtime/turn';
import {applyRuntimeEventToThread} from './store/eventReducer';
import type {Thread} from './store/thread';
import {createEmptyThread} from './store/thread';

/**
 * buildThreadFromTurns reconstructs a `Thread` from a chronologically-sorted
 * array of `TurnResponse`s. All turns are replayed through the event pipeline
 * (`TurnEventExtractor` + `applyRuntimeEventToThread`) in a single Immer
 * produce call, producing an identical `MessageTree` to what live streaming
 * would create.
 */
export function buildThreadFromTurns(
  rootTurnId: string,
  turns: TurnResponse[]
): Thread {
  return produce(createEmptyThread(rootTurnId), draft => {
    for (const turn of turns) {
      const extractor = new TurnEventExtractor(turn.id);
      const events = extractor.extract(turn);
      const now = new Date(turn.created_at).getTime();

      applyRuntimeEventToThread(
        draft,
        {type: 'run-started', runId: turn.id, threadId: rootTurnId},
        now
      );

      for (const event of events) {
        applyRuntimeEventToThread(draft, event, now);
      }

      const terminalEvent = terminalEventForTurnState(
        turn.state,
        turn.id,
        turn.final_output
      );
      if (terminalEvent) {
        applyRuntimeEventToThread(draft, terminalEvent, now);
      }
    }

    draft.rootTurnId = rootTurnId;

    // When a turn was cancelled before the backend recorded any messages,
    // store a descriptive error so the UI can explain why the thread is empty.
    const lastTurn = turns.length > 0 ? turns[turns.length - 1] : null;
    if (
      draft.messageTree.rootChildren.length === 0 &&
      lastTurn?.state === 'cancelled'
    ) {
      draft.error =
        'This conversation was cancelled before the agent could respond.';
    }

    if (turns.length > 0) {
      const first = turns[0];
      const last = turns[turns.length - 1];

      draft.title = decodePromptForThreadTitle(first.user_prompt) || null;
      draft.createdAt = new Date(first.created_at).getTime();
      draft.updatedAt = new Date(last.updated_at).getTime();
    }
  });
}
